import { copyProjectRoom, HotelRoom } from '@/features/projects/projectsSlice';

// Client-side mirror of the server's ValidateRoomNumber (Task-68 §6). Pulled
// into its own dependency-free module, the way languageSelectorValidation.ts
// was, so it can be unit-tested without dragging ProjectRoom.tsx — and the
// whole component tree behind it — into the test run.
//
// The rules gate WRITES only: the server checks the number on a create and on
// a rename, never on an ordinary edit that round-trips the stored number. A
// room already stored under a number these rules would reject therefore keeps
// loading, stays editable and must stay renameable *away* — which is why the
// component asks validateRoomNumberChange(), not validateRoomNumber(), and
// gets back "no issue" whenever the number was not actually changed.

// The cap is on the ENCODED form of the number, not its raw length.
export const MAX_ROOM_NUMBER_ENCODED_BYTES = 200;

// The server trims leading/trailing whitespace BEFORE validating and stores
// the trimmed form, so " 504\t" is a legal way of writing "504". Internal
// whitespace is kept — "Room 101" and "Floor 6/Room 2" are real room numbers
// and must never be stripped (see prepareRoomForSave below).
export function normalizeRoomNumber(room_no: string): string {
  return	room_no
			?	room_no.trim()
			:	'';
}

// Every '/' costs three bytes ("%2F") once the number is encoded into a URL
// path; everything else counts as its plain UTF-8 length. A slash is one
// UTF-8 byte, so the encoded size is the UTF-8 size plus two per slash.
export function roomNumberEncodedLength(room_no: string): number {
  if (!room_no)
    return 0;

  const utf8 = new TextEncoder().encode(room_no).length;
  const slashes = (room_no.match(/\//g) || []).length;

  return utf8 + (slashes * 2);
}

// Returns '' when the number is acceptable, otherwise the translation key of
// the rule it breaks (under projects:project:rooms:room_no). The order of the
// checks follows the server's so the operator sees the same complaint the
// round-trip would have produced.
//
// Deliberately NOT rejected here: forward slashes, internal spaces, unicode
// and dots inside the value. '650/2' and 'Zimmer-Ünf' are legal room numbers.
export function validateRoomNumber(room_no: string): string {
  const trimmed = normalizeRoomNumber(room_no);

  if (trimmed == '')
    return 'err_blank';

  if (	trimmed == '.' ||
		trimmed == '..'
	 )
    return 'err_dot';

  if (trimmed.startsWith('.'))
    return 'err_leading_dot';

  if (trimmed.indexOf('\\') != -1)
    return 'err_backslash';

  // The LITERAL three characters, case-sensitively — a room number may well
  // contain '%2f' as ordinary text, and blocking more than the server does
  // would make a legal number unwritable from the UI.
  if (trimmed.indexOf('%2F') != -1)
    return 'err_encoded_slash';

  for (const ch of trimmed) {
    const code = ch.codePointAt(0) as number;

    if (	code < 0x20 ||
			code == 0x7F
	   )
      return 'err_control';
  }

  if (roomNumberEncodedLength(trimmed) > MAX_ROOM_NUMBER_ENCODED_BYTES)
    return 'err_too_long';

  return '';
}

// True when this save changes the number the room is stored under — the
// server's own rename trigger: old_room_no non-empty and different from
// room_no, with neither dispatch flag set. This is what the page navigates
// on, because the URL segment it resolved the room from stops naming
// anything the moment the rename commits.
export function isRoomRename(room: HotelRoom | null | undefined): boolean {
  if (	!room ||
		room.is_new ||
		room.to_delete
	 )
    return false;

  const old = room.old_room_no ? room.old_room_no : '';

  return	old != '' &&
			old != normalizeRoomNumber(room.room_no);
}

// True when the write will set a number at all — a create, or an edit that
// moved the number away from the stored one. An ordinary edit answers false
// and is never validated, matching the server.
export function isRoomNumberWritten(room: HotelRoom | null | undefined): boolean {
  if (!room)
    return false;

  if (room.is_new)
    return true;

  const old = room.old_room_no ? room.old_room_no : '';

  return normalizeRoomNumber(room.room_no) != old;
}

// The gate the form uses: the §6 rules, but only against a number this save
// actually writes.
export function validateRoomNumberChange(room: HotelRoom | null | undefined): string {
  if (	!room ||
		!isRoomNumberWritten(room)
	 )
    return '';

  return validateRoomNumber(room.room_no);
}

// The body posted to /rooms.json is a FULL-OBJECT REPLACE, so the room is
// copied whole and only the number normalised — never a sparse body, and
// never a mutation of the state object the form is still rendering.
//
// Trim, NOT a global whitespace strip: the previous `replace(/\s+/g, '')`
// also removed INTERNAL spaces while leaving old_room_no alone, so any
// ordinary save of a room legitimately numbered "Room 101" posted
// room_no "Room101" against old_room_no "Room 101" — an unrequested rename.
export function prepareRoomForSave(room: HotelRoom): HotelRoom {
  const out = copyProjectRoom(room);

  out.room_no = normalizeRoomNumber(out.room_no);

  return out;
}
